// TODO Cost line refactor required

import { useFilesValidations } from "common/hooks/useFilesValidations";
import dayjs from "dayjs";
import { costLinesService, useCurrenciesOptions } from "entities/CostLines";
import { toExpenseCostLineSavePayloadTransformer } from "entities/CostLines/lib/transformers/toExpenseCostLinesPayload.transformer";
import { useCostTypeOptions } from "entities/CostType";
import {
  requestFetcher,
  useRequestDependencies,
  useRequestPersistedStore,
} from "entities/Request";
import { useRequestLinesStore } from "entities/TravelLines";
import { useInternalOrderStore } from "features/SelectInternalOrder";
import {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { baseApiService } from "shared/api";
import { ROLES } from "shared/config";
import {
  toVatCodesOptionsTransformer,
  toVatLabelTransformer,
} from "shared/lib";
import { toFormData } from "shared/utils";
import useSWRMutation from "swr/mutation";
import { expensesCostLineDefaultValues } from "../../../../modules/Expenses/constants";
import { getOnSaveValidationSchema } from "../../../../modules/Expenses/validations/getOnSaveValidationSchema";
import { getOnSubmitValidationSchema } from "../../../../modules/Expenses/validations/getOnSubmitValidationSchema";
import { useCostLinesStore } from "../../store/useCostLinesStore";
import ExpensesRequestCostLineFormView from "./ExpensesRequestCostLine.view";
import { useAvailableModules } from "shared/api/hooks/useAvailableModules";

const ExpensesRequestCostLineContainer = forwardRef(
  function ExpensesRequestCostLineContainer(props, ref) {
    const {
      // @ts-ignore
      request,
      // @ts-ignore
      costLineData = null,
      // @ts-ignore
      disabled = false,
      // @ts-ignore
      access,
      // @ts-ignore
      footer,
      // @ts-ignore
      idx,
    } = props;
    const updateCostLineState = useCostLinesStore(
      (state) => state.updateCostLineState
    );
    const addRequestLineToStack = useRequestLinesStore(
      (state) => state.addRequestLineToStack
    );
    const clearCostLineState = useCostLinesStore(
      (state) => state.clearCostLineState
    );
    const viewRequestAs = useRequestPersistedStore(
      (state) => state.viewRequestAs
    );
    const internalOrder = useInternalOrderStore((state) => state.internalOrder);
    const updateCostLineById = useCostLinesStore(
      (state) => state.updateCostLineById
    );

    const { data: modules } = useAvailableModules();

    const { t } = useTranslation(["CostLine"]);

    const { trigger } = useSWRMutation(
      [
        baseApiService.ENDPOINTS.request.shared.request,
        request?.number,
        viewRequestAs.role,
      ],
      requestFetcher
    );

    const { data: { dependencies, validations } = {} } = useRequestDependencies(
      "cost_line_form",
      request?.id,
      {
        triggerRequest:
          // TODO remove hardcoding and implement available modules endpoint
          ["E600", "I621", "I62U", "L621", "R622", "R62E"].includes(
            request?.cost_centre?.slice(0, 4)
          ) || access.to("CostLine", "edit").provided(),
      }
    );

    // * ----------------
    // *  VALIDATIONS
    // * ----------------

    const { filesValidationSchema, setFilesValidations } =
      useFilesValidations();

    useEffect(() => {
      if (
        validations?.attachments.attachments_allowed_file_types?.data.length
      ) {
        setFilesValidations((prev) => ({
          files: {
            ...prev.files,
            attachments_allowed_file_types:
              validations?.attachments.attachments_allowed_file_types.data,
            attachments_count_rules:
              validations?.attachments.attachments_count_rules.data,
            max_allowed_file_size_in_bytes:
              validations?.attachments.max_allowed_file_size_in_bytes,
          },
        }));
      }
    }, [validations]);

    const onSaveResolver = useMemo(
      () => getOnSaveValidationSchema(filesValidationSchema),
      [filesValidationSchema]
    );
    const onSubmitResolver = useMemo(
      () => getOnSubmitValidationSchema(filesValidationSchema),
      [filesValidationSchema]
    );

    // useEffect below is a workaround for setting the correct initial validation of attachments
    // TODO remove after MulitFilesInput removal in favor of FilesInput
    useEffect(() => {
      resolver.current = onSaveResolver;
    }, [onSaveResolver]);

    const resolver = useRef(onSaveResolver);

    const form = useForm({
      resolver: (...args) => resolver.current(...args),
      defaultValues: expensesCostLineDefaultValues,
    });

    const {
      handleSubmit,
      reset,
      watch,
      formState: { errors, isDirty, dirtyFields },
      unregister,
      setValue,
      resetField,
    } = form;

    const isJobRelated = watch("is_job_related");
    const costType = watch("cost_type");
    const expenseDoctype = watch("expense_document_type");

    const costTypeOptions = useCostTypeOptions({
      costTypes: dependencies?.cost_types?.data,
      costLineData,
      isJobRelated,
    });

    useEffect(() => {
      if (!isJobRelated) {
        unregister("invoice_number");
      }
    }, [isJobRelated]);

    useEffect(() => {
      if (costLineData.application_id) {
        reset(
          Object.fromEntries(
            Object.entries(expensesCostLineDefaultValues).map(
              ([field, value]) => {
                let newValue = costLineData?.[field] ?? value;
                if (
                  field === "internal_order_id" &&
                  internalOrder &&
                  internalOrder.value
                ) {
                  newValue = internalOrder.value;
                }
                if (field === "purchase_date" && newValue) {
                  newValue = dayjs(newValue);
                }
                if (field === "attachments")
                  newValue = costLineData.attachments.data;

                if (field === "cost_type") {
                  if (newValue?.data?.id) {
                    newValue = {
                      value: {
                        id: newValue.data.id,
                        subtype: newValue.data.subtype,
                      },
                      vatCodes: newValue.data.available_vat_codes.data,
                      label: newValue.data.name,
                    };
                    // ! Empty cost type is returned as an empty array
                  } else newValue = expensesCostLineDefaultValues.cost_type;
                }
                if (field === "currency_code") {
                  if (newValue) {
                    newValue = { value: newValue, label: newValue };
                  }
                }
                if (field === "vat") {
                  if (newValue?.data?.id) {
                    newValue = {
                      value: newValue.data.id,
                      label: toVatLabelTransformer(newValue.data),
                    };
                    // ! Empty vat is returned as an empty array
                  } else newValue = expensesCostLineDefaultValues.vat;
                }

                return [field, newValue];
              }
            )
          )
        );
      } else {
        let costType = null;
        if (costLineData?.accommodation_required_line) {
          const accomodationCost = dependencies?.cost_types.data.find(
            (costType) => costType.subtype === "Accommodation"
          );
          costType = {
            value: {
              id: accomodationCost?.id,
              subtype: accomodationCost?.subtype,
            },
            vatCodes: accomodationCost?.available_vat_codes?.data,
            label: accomodationCost?.name,
          };
        }

        let currencyCode = expensesCostLineDefaultValues.currency_code;

        if (isCurrencySelectDisabled) {
          currencyCode = {
            value: dependencies?.currencies?.data[0]?.code,
            label: dependencies?.currencies?.data[0]?.code,
          };
        }

        reset({
          ...expensesCostLineDefaultValues,
          cost_type: costType,
          application_id: request?.id,
          currency_code: currencyCode,
          payment_method_type:
            validations?.allowed_payment_methods?.data[0]?.name,
          internal_order_id:
            internalOrder && internalOrder.value
              ? internalOrder.value
              : costLineData?.internal_order_id ?? null,
          accommodation_required_line:
            costLineData?.accommodation_required_line,
        });
      }
      return () => {
        clearCostLineState(costLineData.id);
      };
    }, [costLineData, dependencies]);

    useEffect(() => {
      if (
        internalOrder?.value &&
        costLineData.internal_order_id !== internalOrder?.value
      ) {
        setValue("internal_order_id", internalOrder.value, {
          shouldDirty: true,
        });
      }
    }, [internalOrder?.value, costLineData.internal_order_id]);

    useEffect(() => {
      updateCostLineState(costLineData.id, {
        isModified: isDirty || costLineData.id.toString().startsWith("initial"),
        hasErrors: !!Object.keys(errors).length,
      });
    }, [isDirty, errors, costLineData]);

    //*-------------
    //*  HANDLERS
    //*-------------

    const handleRequestLineSave = () => {
      addRequestLineToStack(
        new Promise((resolve) => {
          handleSubmit(
            // @ts-ignore
            (formData) =>
              resolve(
                toExpenseCostLineSavePayloadTransformer({
                  costLineData,
                  formData,
                  isDirty: isDirty,
                  dirtyFields: dirtyFields,
                  userRole: viewRequestAs.role,
                  costType,
                })
              ),
            (errors) => resolve({ isValid: false, errors })
          )();
        })
      );
    };

    useImperativeHandle(ref, () => ({
      save() {
        resolver.current = onSaveResolver;
        handleRequestLineSave();
      },
      submit() {
        resolver.current = onSubmitResolver;
        handleRequestLineSave();
      },
      accept() {
        resolver.current = onSaveResolver;
        if (
          access
            .to("CostLine", "editExpenseDoctype")
            .withCostLineValidation(costLineData.id)
            .provided() &&
          expenseDoctype === null
        ) {
          return toast.error(
            t("toasts.expense_doctype_is_required", { ns: "CostLine" }),
            {
              theme: "colored",
              autoClose: 10000,
            }
          );
        }

        handleSubmit(
          // @ts-ignore
          async (formData) => {
            const payload = toExpenseCostLineSavePayloadTransformer({
              costLineData,
              formData,
              isDirty: isDirty,
              dirtyFields: dirtyFields,
              userRole: viewRequestAs.role,
              costType,
            });

            updateCostLineState(costLineData.id, { isAccepting: true });

            if (viewRequestAs.role === ROLES.ACCOUNTS_PAYABLE && isDirty) {
              const updateCostLineResponse =
                await costLinesService.updateCostLine(
                  toFormData(payload.formData)
                );
              if (updateCostLineResponse?.status === 200) {
                updateCostLineById(costLineData.id, {
                  ...updateCostLineResponse?.data?.data,
                });

                trigger();
              } else {
                updateCostLineState(costLineData.id, { isAccepting: false });
                return;
              }
            }

            const acceptCostLineResponse =
              await costLinesService.acceptCostLine({
                cost_line_id: costLineData.id,
                user_role: viewRequestAs.role,
              });
            if (acceptCostLineResponse?.status === 200) {
              updateCostLineById(
                costLineData.id,
                acceptCostLineResponse.data?.data
              );
            }
            updateCostLineState(costLineData.id, { isAccepting: false });
          }
        )();
      },
    }));

    useEffect(() => {
      const costTypeData = dependencies?.cost_types?.data?.find(
        (ct) => ct?.id === costType?.value?.id
      );
      if (costTypeData) {
        // Hardcoded directly by "Glasses, contact lenses" cost name 🤡
        // TODO remove hardcoding
        if (costTypeData?.name === "Glasses, contact lenses") {
          setValue("currency_code", { value: "PLN", label: "PLN" });
        }
        if (costType?.value?.id !== costLineData?.cost_type_id) {
          if (costTypeData?.available_vat_codes?.data?.length === 1) {
            const firstVatCode = costTypeData?.available_vat_codes?.data[0];
            const formattedVatCode = {
              value: firstVatCode.id,
              label: toVatLabelTransformer(firstVatCode),
            };
            setValue("vat", formattedVatCode);
          } else {
            resetField("vat", {
              defaultValue: expensesCostLineDefaultValues.vat,
            });
          }
        }
      }
    }, [costType?.value?.id]);

    const isMileageCost = costType?.value.subtype === "Mileage";

    const vatCodesOptions = toVatCodesOptionsTransformer(costType?.vatCodes);

    const currenciesOptions = useCurrenciesOptions({
      dependencies,
      requestType: request?.type,
    });

    const isCurrencySelectDisabled =
      disabled ||
      // Hardcoded directly by "Glasses, contact lenses" cost name 🤡
      // TODO remove hardcoding
      costType?.label === "Glasses, contact lenses" ||
      dependencies?.currencies?.data?.length === 1 ||
      isJobRelated;

    const isCostTypeSelectDisabled =
      disabled ||
      costLineData?.accommodation_required_line ||
      (isJobRelated && costTypeOptions.length === 1);

    const isVatSelectDisabled =
      (disabled || (vatCodesOptions?.length === 1 && !isJobRelated)) &&
      access.to("CostLine", "selectVatExclusively").unprovided();

    return (
      <div className="my-4">
        <ExpensesRequestCostLineFormView
          idx={idx}
          form={form}
          isJobRelatedAllowed={request.is_job_related_allowed}
          costLineData={costLineData}
          handleSubmit={handleSubmit}
          isMileageCost={isMileageCost}
          vehicleOptions={dependencies?.vehicles?.options}
          validations={validations}
          vatCodesOptions={vatCodesOptions}
          disabled={disabled}
          viewRequestAs={viewRequestAs}
          errors={errors}
          mileageUnit={dependencies?.mileage_unit}
          displayExpenseDoctypes={access
            .to("CostLine", "viewExpenseDoctype")
            .withCostLineValidation(costLineData.id)
            .provided()}
          expenseDoctypes={dependencies?.expense_document_types?.data}
          isExpenseDoctypeCheckDisabled={access
            .to("CostLine", "editExpenseDoctype")
            .withHold()
            .withLock()
            .withCostLineValidation(costLineData.id)
            .unprovided()}
          currenciesOptions={currenciesOptions}
          isCurrencySelectDisabled={isCurrencySelectDisabled}
          isCompanyCardPaymentAllowed={validations?.company_card_allowed}
          isCostTypeSelectDisabled={isCostTypeSelectDisabled}
          isVatSelectDisabled={isVatSelectDisabled}
          isJobRelated={isJobRelated}
          costTypeOptions={costTypeOptions}
          displayCurrencyExchange={
            modules?.user_modules?.CurrencyModule?.available &&
            viewRequestAs.role === ROLES.USER &&
            !isMileageCost &&
            !disabled
          }
        />
        {footer}
      </div>
    );
  }
);

export default memo(ExpensesRequestCostLineContainer);
